import { apiAuthorization } from 'constants/api';
import { AppServices, ServiceConfig } from 'interfaces';
import { getTenantId } from 'packmanager-communication-lib';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { getPacksInfo } from 'services/Packs/getPacksInfo';
import { initializeSegment } from 'services/Segment';
import { ServiceFactory } from 'services/ServiceFactory';

export interface AppContextData {
    appServices: AppServices;
}

interface AppContextValue {
    children: any;
}

export const AppContext = createContext<AppContextData>({} as AppContextData);

export const AppProvider: React.FC<AppContextValue> = ({ children }) => {
    const [initialized, setInitialized] = useState(false);
    const [appServices, setAppServices] = useState<AppServices | null>(null);

    const setApis = async () => {
        const { accessToken, packId } = await getPacksInfo();
        const tenant = (await getTenantId()) || 'dev-tenant';
        initializeSegment();
        const config: ServiceConfig = {
            tenant,
            packId,
            userToken: accessToken,
            apiToken: apiAuthorization || '',
        };
        setAppServices(new ServiceFactory(config).getServices());
        setInitialized(true);
    };

    useEffect(() => {
        setInitialized(false);
        setApis();
    }, []);

    const appContextValue = useMemo(() => ({ appServices }), [appServices]);

    return initialized && appServices ? (
        <AppContext.Provider value={appContextValue as AppContextData}>{children}</AppContext.Provider>
    ) : (
        <></>
    );
};

export function useAppContext(): AppContextData {
    const context = React.useContext(AppContext);

    if (!context.appServices) {
        throw new Error('use app context must be used within an AppProvider');
    }

    return context;
}
